<style scoped>
.html-container {
  padding: 2rem;
}
</style>

<template>
  <div>
    <div v-if="(isAdmin || $store.getters.isAdmin) && !isPreview">
      <input type="file" id="file" ref="file" @change="onFileChange" class="display-none"/>
      <div class="editor" >
        <editor-menu-bubble class="menububble" :editor="editor">
        <div
          slot-scope="{ commands, isActive, getMarkAttrs, menu }"
          class="menububble"
          :class="{ 'is-active': menu.isActive }"
          :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
        >

          <form class="menububble__form" v-if="linkMenuIsActive" @submit.prevent="setLinkUrl(commands.link, linkUrl)">
          <input class="menububble__input" type="text" v-model="linkUrl" placeholder="https://" ref="linkInput" @keydown.esc="hideLinkMenu"/>
          </form>

          <template v-else>
          <button
            class="menububble__button"
            @click="showLinkMenu(getMarkAttrs('link'))"
            :class="{ 'is-active': isActive.link() }"
          >
            <span>Link</span>
          </button>
          </template>

        </div>
        </editor-menu-bubble>
        <editor-menu-bar :editor="editor">
        <div class="menubar" slot-scope="{ commands, isActive }">

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.bold() }"
          @click="commands.bold"
          >
          <mu-icon value="format_bold" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.italic() }"
          @click="commands.italic"
          >
          <mu-icon value="format_italic" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.strike() }"
          @click="commands.strike"
          >
          <mu-icon value="format_strikethrough" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.underline() }"
          @click="commands.underline"
          >
          <mu-icon value="format_underlined" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.code() }"
          @click="commands.code"
          >
          <mu-icon value="code" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.paragraph() }"
          @click="commands.paragraph"
          >
          <mu-icon value="format_textdirection_l_to_r" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click="commands.heading({ level: 1 })"
          >
          <mu-icon value="title" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click="commands.heading({ level: 2 })"
          >
          <mu-icon value="text_fields" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click="commands.bullet_list"
          >
          <mu-icon value="format_list_bulleted" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click="commands.ordered_list"
          >
          <mu-icon value="format_list_numbered" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.blockquote() }"
          @click="commands.blockquote"
          >
          <mu-icon value="format_quote" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          :class="{ 'is-active': isActive.code_block() }"
          @click="commands.code_block"
          >
          <mu-icon value="code" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          @click="commands.undo"
          >
          <mu-icon value="undo" />
          </mu-button>

          <mu-button icon small 
          class="menubar__button"
          @click="commands.redo"
          >
          <mu-icon value="redo" />
          </mu-button>
          <mu-button icon small 
          class="menubar__button"
          @click="showImagePrompt(commands.image)"
          >
          <mu-icon value="image" />
          </mu-button>
        </div>
        </editor-menu-bar>

        <editor-content class="editor__content" :editor="editor" @onUpdate="onUpdate"/>
      </div>
      
    </div>
    <div v-else v-html="computedHtml" class="html-container"/>
  </div>
</template>

<script>
// Library
import axios from 'axios'

// Import the editor
import { Editor, EditorContent, EditorMenuBar, EditorMenuBubble } from 'tiptap'
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Image,
} from 'tiptap-extensions'

export default {
  name: 'WysiwygItem',
  components: {
    EditorContent,
    EditorMenuBar, 
    EditorMenuBubble
  },
  props: {
    media: {
      type: Object,
      required: true
    },
    isAdmin: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      modelHtml: this.media.content ? this.media.content : "",
      tab: 0,

      timeout: null,
      editor: null,
      linkUrl: null,
      linkMenuIsActive: false,
      image: null,
      file: null,
    }
  },
  watch: {
    modelHtml(newValue) {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        let formData = new FormData();
        formData.append('_method', "PUT");
        formData.append('content', newValue);
        formData.append('type', 'wysiwyg');
        axios.post(this.$store.getters.urlApi + "media/" + this.media._id, formData, {
          headers: {
            token: this.$store.state.auth.token
          },
          }
        );
      }, 500);
    },
    media: {
      handler(newValue) {
        this.modelHtml = newValue.content ? newValue.content : '';
        this.editor.setContent(this.modelHtml);
      },
      deep: true
    },
    isPreview() {
      this.initEditor();
    },
  },
  computed: {
    computedHtml() {
      if(typeof this.modelHtml === 'string' ) {
        return this.modelHtml.replace('<img', '<img style="max-width: 100%;"').replace('<a', '<a target="_blank"');
      } else {
        return "";
      }
    },
    isPreview() {
      if(this.$store.getters.previewMode) {
        return true;
      } else {
        return false;
      }
    },
  },
  mounted() {
    this.initEditor();
  },
  methods: {
    initEditor() {
      this.editor = new Editor({
        content: this.modelHtml,
        extensions: [
        new Blockquote(),
        new BulletList(),
        new CodeBlock(),
        new HardBreak(),
        new Heading({ levels: [1, 2, 3] }),
        new ListItem(),
        new OrderedList(),
        new TodoItem(),
        new TodoList(),
        new Bold(),
        new Code(),
        new Italic(),
        new Link(),
        new Strike(),
        new Underline(),
        new History(),
        new Image(),
        ],
        onUpdate: ({ getJSON, getHTML }) => {
          this.modelHtml = getHTML()
        },
      });
    },
    showLinkMenu(attrs) {
    this.linkUrl = attrs.href
    this.linkMenuIsActive = true
    this.$nextTick(() => {
      this.$refs.linkInput.focus()
    })
    },
    hideLinkMenu() {
    this.linkUrl = null
    this.linkMenuIsActive = false
    },
    setLinkUrl(command, url) {
    console.log(url)
    command({ href: url })
    this.hideLinkMenu()
    this.editor.focus()
    },
    showImagePrompt(command) {
    this.image = command;
    /*const src = prompt('Enter the url of your image here')
    if (src !== null) {
      command({ src })
    }*/
    this.$refs.file.click();
    },
    onFileChange() {
    if (this.$refs.file.files[0] != undefined) {
      this.file = this.$refs.file.files[0];
      let formData = new FormData();
      formData.append('file', this.file);
      axios.post(this.$store.getters.urlApi + 'attachment/image', formData, {
        headers: {
          token: this.$store.state.auth.token
        }
        }
      ).then(res => {
        var src = res.data;
        this.image({ src });
      });
    } else {
      this.file = null;
    }
    },
    onUpdate(state) {
    
    }
  },
  beforeDestroy() {
    this.editor.destroy()
  },
}
</script>
